// WHAT THE PRINTED QR CODE OPENS (TW-0206) — the form's own reading of the two keys, where "Pay a deposit" goes, and
// the one rule for when the choice is drawn.
//
// Chris, 2026-09-23: "Also I got to thinking that Your QR Code likely ends up defaulting to Waivers but maybe they can
// make it also allow a user to pick. Similar to Linktree right I mean if enabled their QR code can show all of their
// enabled things. Not now but assumedly we end up with deposits shops and waivers shop and some overlap."
//
// THE PRINTED CODE NEVER CHANGES. It opens `<shop>.waivers.tattoo`, this bundle, exactly as it has since 2023; what
// changes, for a shop whose owner turned the switch on and whose deposit page is taking deposits, is the FIRST STATE
// of this page: the shop's mark, then Sign your waiver · Pay a deposit (QrPicker.tsx). One tap on the first is the
// form, on the same page, with no reload. The second leaves for the shop's deposit page.
//
// THE DEFAULT IS THE FORM, BYTE FOR BYTE. A shop whose config lacks either key — every shop that never touched the
// switch, and each of the six — never mounts the choice at all (QrPicker.test.tsx says so against the rendered page).

/** The two keys as they arrive on `public_data`: the owner's switch, and the deposit page's own answer as the shop app
 *  carried it (the API's common.py header says who writes which). */
export type PickerKeys = { qr_picker?: unknown; deposits_on?: unknown } | null | undefined;

/** Is the choice offered? The owner's switch AND the deposit page taking deposits, each a LITERAL true — the API's
 *  common.qr_picker_shows and the shop app's lib/qr-picker.ts pickerLive read it the same way, so a hand-written
 *  "true" or a 1 on a row is off everywhere at once. */
export function offersPicker(publicData: PickerKeys): boolean {
  return publicData?.qr_picker === true && publicData?.deposits_on === true;
}

/** Where the deposit page is on a laptop (`./run.sh dev-deposit-page`), which reads `?shop=` the way this bundle does. */
export const LOCAL_DEPOSIT_PAGE = "http://localhost:3003";

/** Where "Pay a deposit" goes: the shop's deposit page, the same subdomain on deposits.tattoo (docs/DEPOSITS.md → The
 *  surfaces). On a dev server, the local deposit page with `?shop=` — never the real wildcard host, where a shop minted
 *  on the dev tables has no page. NULL for a subdomain that is not a DNS label, and then the choice is never drawn: a
 *  button that goes nowhere is worse than the form. */
export function depositPageUrl(subdomain: string, env: string | undefined = process.env.NODE_ENV): string | null {
  const sub = `${subdomain ?? ""}`.trim().toLowerCase();
  if (!/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(sub)) return null;
  if (env === "development") return `${LOCAL_DEPOSIT_PAGE}/?shop=${sub}`;
  return `https://${sub}.deposits.tattoo/`;
}

/** Where this page load stands with the choice:
 *    undecided — neither source has answered, so the placeholder form is up and nothing is known yet
 *    shown     — the choice is drawn instead of the form
 *    hidden    — the form, for the rest of this load
 *    picked    — the client tapped Sign your waiver: the form, for the rest of this load */
export type PickerState = "undecided" | "shown" | "hidden" | "picked";

export type PickerInputs = {
  /** neither the static config nor the API has answered yet */
  placeholder: boolean;
  /** offersPicker, on the shop as it stands NOW (the latest source to land) */
  offers: boolean;
  /** the form's own route, a real shop (never the demo) and a deposit page to send anyone to */
  eligible: boolean;
  /** the client has already been in a field of the placeholder form */
  touched: boolean;
};

/** The one rule, as a function of where the page stands and what just landed.
 *
 *  THE CHOICE IS ONLY EVER THE FIRST THING A CLIENT SEES, NEVER A THING THAT INTERRUPTS. It can be drawn once, at the
 *  first real answer, and only for a client who has not already started on the placeholder form (on a slow line the
 *  API's answer can take two seconds, and a client typing their phone number must not lose it to a menu). After that it
 *  can only go AWAY: a fresher answer that no longer offers it (the API's `/shop` wins over the static file) takes it
 *  down before anyone chose, and a form on screen is never taken from anyone — `hidden` and `picked` are final. */
export function nextPickerState(current: PickerState, input: PickerInputs): PickerState {
  if (current === "hidden" || current === "picked") return current;
  if (input.placeholder) return current;
  if (current === "undecided") return input.eligible && input.offers && !input.touched ? "shown" : "hidden";
  return input.eligible && input.offers ? "shown" : "hidden";
}
